import type {
  PostAdminV1StoresStoreIdQcTemplatesBody,
  PostAdminV1StoresStoreIdWorkflowTemplatesBody,
  PostAdminV1StoresStoreIdWorkflowTemplatesBodyStagesItem,
  QcField,
  QcTemplate,
  WorkflowStageEmail,
  WorkflowSubStep,
  WorkflowTemplate,
} from '@nucleus-commerce/api-client/admin';

import { toDurationParts, toMinutes } from '../schemas/duration';
import {
  NO_PREFILL,
  type QcFieldFormValues,
  type QcFormInput,
  type QcFormValues,
} from '../schemas/qc-form-schema';
import {
  CUSTOMER_STAGE_TYPES,
  type StageFormValues,
  type SubStepFormValues,
  type WorkflowFormInput,
  type WorkflowFormValues,
} from '../schemas/workflow-form-schema';

/** "Diamond sourcing" → "diamond_sourcing"; empty when the name has no letters or digits. */
export function keyFromName(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 60)
    .replace(/_+$/g, '');
}

/**
 * Hands out keys: typed keys are kept as they are (the backend reports duplicates on the field),
 * blank ones are derived from the name and made unique against everything else in the set.
 */
function keyAllocator(explicit: string[]) {
  const taken = new Set(explicit.filter(Boolean));
  return (key: string, name: string, fallback: string) => {
    if (key) return key;
    const base = keyFromName(name) || fallback;
    let candidate = base;
    for (let n = 2; taken.has(candidate); n += 1) candidate = `${base}_${n}`;
    taken.add(candidate);
    return candidate;
  };
}

const textOrNull = (value: string) => (value.trim() === '' ? null : value.trim());

function emailToForm(email: WorkflowStageEmail) {
  return { emailWhen: email.when, emailSubject: email.subject ?? '', emailBody: email.body ?? '' };
}

function emailToBody(values: Pick<StageFormValues, 'emailWhen' | 'emailSubject' | 'emailBody'>) {
  return {
    when: values.emailWhen,
    subject: textOrNull(values.emailSubject),
    body: textOrNull(values.emailBody),
  };
}

function subStepToForm(sub: WorkflowSubStep): SubStepFormValues {
  const duration = toDurationParts(sub.durationMinutes);
  return {
    key: sub.key,
    name: sub.name,
    customerName: sub.customerName ?? '',
    hidden: sub.hidden,
    durationValue: duration.value,
    durationUnit: duration.unit,
    optional: sub.optional,
    alertUserIds: sub.alertUserIds,
    ...emailToForm(sub.email),
  };
}

export function fromWorkflowTemplate(template: WorkflowTemplate): WorkflowFormInput {
  return {
    name: template.name,
    stages: template.stages.map((stage): StageFormValues => {
      const duration = toDurationParts(stage.durationMinutes);
      const limit = toDurationParts(stage.responseLimitMinutes);
      return {
        key: stage.key,
        name: stage.name,
        customerName: stage.customerName ?? '',
        hidden: stage.hidden,
        type: stage.type,
        durationValue: duration.value,
        durationUnit: duration.unit,
        optional: stage.optional,
        alertUserIds: stage.alertUserIds,
        ...emailToForm(stage.email),
        subSteps: stage.subSteps.map(subStepToForm),
        responseLimitValue: limit.value,
        responseLimitUnit: limit.unit,
        qcTemplateId: stage.qcTemplateId ?? '',
        parcels: stage.parcels.map((parcel) => ({ ...parcel })),
        // Snapshots taken before the flag existed read as approving.
        approvesDesign: stage.approvesDesign ?? true,
      };
    }),
  };
}

export function toWorkflowTemplateBody(
  values: WorkflowFormValues,
): Pick<PostAdminV1StoresStoreIdWorkflowTemplatesBody, 'name' | 'stages'> {
  const allocate = keyAllocator(
    values.stages.flatMap((stage) => [stage.key, ...stage.subSteps.map((sub) => sub.key)]),
  );
  const stages = values.stages.map(
    (stage, index): PostAdminV1StoresStoreIdWorkflowTemplatesBodyStagesItem => {
      const customer = CUSTOMER_STAGE_TYPES.includes(stage.type);
      const parcelKeys = keyAllocator(stage.parcels.map((parcel) => parcel.key));
      return {
        key: allocate(stage.key, stage.name, `stage_${index + 1}`),
        name: stage.name,
        customerName: textOrNull(stage.customerName),
        hidden: stage.hidden,
        type: stage.type,
        durationMinutes: toMinutes(stage.durationValue, stage.durationUnit),
        optional: stage.optional,
        alertUserIds: stage.alertUserIds,
        email: emailToBody(stage),
        subSteps:
          stage.type === 'task'
            ? stage.subSteps.map((sub, subIndex) => ({
                key: allocate(sub.key, sub.name, `step_${index + 1}_${subIndex + 1}`),
                name: sub.name,
                customerName: textOrNull(sub.customerName),
                hidden: sub.hidden,
                durationMinutes: toMinutes(sub.durationValue, sub.durationUnit),
                optional: sub.optional,
                alertUserIds: sub.alertUserIds,
                email: emailToBody(sub),
                type: 'task',
              }))
            : [],
        qcTemplateId: stage.type === 'qc' ? stage.qcTemplateId || null : null,
        parcels:
          stage.type === 'shipping'
            ? stage.parcels.map((parcel, parcelIndex) => ({
                key: parcelKeys(parcel.key, parcel.label, `parcel_${parcelIndex + 1}`),
                label: parcel.label,
                carriesItems: parcel.carriesItems,
              }))
            : [],
        responseLimitMinutes: customer
          ? toMinutes(stage.responseLimitValue, stage.responseLimitUnit)
          : null,
        ...(stage.type === 'customer_review' ? { approvesDesign: stage.approvesDesign } : {}),
      };
    },
  );
  return { name: values.name, stages };
}

const FORM_FIELD: Record<string, string> = {
  durationMinutes: 'durationValue',
  responseLimitMinutes: 'responseLimitValue',
  'email.subject': 'emailSubject',
  'email.body': 'emailBody',
  'email.when': 'emailWhen',
  email: 'emailBody',
};

/**
 * Backend paths come as `stages[2].subSteps[0].email.subject` (service checks) or
 * `stages.2.key` (schema validation); both land on the editor's own field names.
 */
export function workflowFieldPath(apiField: string): string {
  const dotted = apiField.replace(/\[(\d+)\]/g, '.$1');
  return dotted.replace(
    /(^|\.)(durationMinutes|responseLimitMinutes|email\.subject|email\.body|email\.when|email)$/,
    (_match, dot: string, name: string) => `${dot}${FORM_FIELD[name] ?? name}`,
  );
}

/** Every path the editor renders an error for; anything else goes to the form-level alert. */
export function knownWorkflowPaths(values: WorkflowFormInput): Set<string> {
  const paths = new Set<string>(['name', 'stages']);
  const stageFields = [
    'key',
    'name',
    'customerName',
    'type',
    'durationValue',
    'alertUserIds',
    'emailSubject',
    'emailBody',
    'emailWhen',
    'responseLimitValue',
    'qcTemplateId',
    'parcels',
    'subSteps',
  ];
  values.stages.forEach((stage, index) => {
    for (const field of stageFields) paths.add(`stages.${index}.${field}`);
    stage.subSteps.forEach((_sub, subIndex) => {
      for (const field of [
        'key',
        'name',
        'durationValue',
        'alertUserIds',
        'emailSubject',
        'emailBody',
      ]) {
        paths.add(`stages.${index}.subSteps.${subIndex}.${field}`);
      }
    });
    stage.parcels.forEach((_parcel, parcelIndex) => {
      for (const field of ['key', 'label'])
        paths.add(`stages.${index}.parcels.${parcelIndex}.${field}`);
    });
  });
  return paths;
}

function qcFieldToForm(field: QcField): QcFieldFormValues {
  return {
    key: field.key,
    label: field.label,
    type: field.type,
    options: field.options.map((value) => ({ value })),
    allowOther: field.allowOther,
    required: field.required,
    prefill: field.prefill ?? NO_PREFILL,
  };
}

export function fromQcTemplate(checklist: QcTemplate): QcFormInput {
  return { name: checklist.name, fields: checklist.fields.map(qcFieldToForm) };
}

/** The form value of one field as the API's QcField, for previews. */
export function toQcField(field: QcFieldFormValues, index: number): QcField {
  return {
    key: field.key || keyFromName(field.label) || `field_${index + 1}`,
    label: field.label,
    type: field.type,
    options: field.type === 'choice' ? field.options.map((o) => o.value).filter(Boolean) : [],
    allowOther: field.type === 'choice' && field.allowOther,
    required: field.required,
    prefill: field.type === 'file' || field.prefill === NO_PREFILL ? null : field.prefill,
  };
}

export function toQcTemplateBody(values: QcFormValues): PostAdminV1StoresStoreIdQcTemplatesBody {
  const allocate = keyAllocator(values.fields.map((field) => field.key));
  return {
    name: values.name,
    fields: values.fields.map((field, index) => ({
      ...toQcField(field, index),
      key: allocate(field.key, field.label, `field_${index + 1}`),
    })),
  };
}

export function qcFieldPath(apiField: string): string {
  return apiField.replace(/\[(\d+)\]/g, '.$1').replace(/^(fields\.\d+\.options\.\d+)$/, '$1.value');
}

export function knownQcPaths(values: QcFormInput): Set<string> {
  const paths = new Set<string>(['name', 'fields']);
  values.fields.forEach((field, index) => {
    for (const name of ['key', 'label', 'type', 'options', 'prefill'])
      paths.add(`fields.${index}.${name}`);
    field.options.forEach((_option, optionIndex) =>
      paths.add(`fields.${index}.options.${optionIndex}.value`),
    );
  });
  return paths;
}
